/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

import { ComponentProps, RefObject, JSX } from 'preact';
import { useCallback, useRef } from 'preact/hooks';

import { Floating } from '../UNSAFE_Floating';
import { Layer } from '../UNSAFE_Layer';
import { KEYS } from '../utils/PRIVATE_keyboardUtils';

import { dropdownStyles } from './themes/dropdownStyles.css';
import { useOutsideMousedown } from '../hooks/PRIVATE_useOutsideMousedown';

type FloatingProps = ComponentProps<typeof Floating>;

//Same as Menu onClose, only difference is mouseDown instead of outside click
//Once menu consumes dropdown is going to start using mouseDown
type CloseDetail = { reason: 'dismissed' } | { reason: 'outsideMousedown'; target: Element };

type IntrinsicProps = Pick<JSX.HTMLAttributes<HTMLDivElement>, 'children'>;

type Props = IntrinsicProps & {
  /**
   *Specifies whether Dropdown is open.
   */
  isOpen?: boolean;
  /**
   * Trigger element reference.
   */
  anchorRef: FloatingProps['anchorRef'];
  /**
   * Id of the dropdown container.
   * Used usually for accesibility reasons.
   */
  id?: string;
  /**
   * Called when dropdown is supposed to be closed.
   * Called when user tab/esc or mouseDown outside the dropdown.
   */
  onClose?: (detail: CloseDetail) => void;
  /**
   *Min width represented in pixels of the dropdown container.
   */
  minWidth?: number;
  /**
   * Placement of the floating element relative to its reference element.
   */
  placement?: FloatingProps['placement'];
  /**
   * Offset displaces the floating element from its core placement along the specified axes.
   */
  offset?: FloatingProps['offsetValue'];
  /**
   * Triggered when placement or coordinates are changed after collision is detected.
   */
  onPosition?: FloatingProps['onPosition'];
  /**
   * Specifies the flipping behavior along the respective axis.
   */
  flipOptions?: FloatingProps['flipOptions'];
  /**
   * Specifies the resizing behaviour for max-height and max-width of the floating component.
   */
  sizeOptions?: FloatingProps['sizeOptions'];
};

const DEFAULT_PLACEMENT = 'bottom-start';
const DEFAULT_OFFSET = { mainAxis: 4, crossAxis: 0 };
const DEFAULT_SIZE_OPTIONS = { isMaxWidthAdjusted: true, isMaxHeightAdjusted: true };

//TODO: Add animation to dropdown. JET-62628
//TODO: Investigate best approach to have different virtual padding depending on the position and implement it.JET-57950
export function Dropdown({
  isOpen = false,
  anchorRef,
  children,
  id,
  onClose,
  minWidth,
  placement = DEFAULT_PLACEMENT,
  offset = DEFAULT_OFFSET,
  onPosition,
  flipOptions,
  sizeOptions = DEFAULT_SIZE_OPTIONS
}: Props) {
  const dropdownRef = useRef(null);

  const handleMousedownClose = useCallback(
    (event: MouseEvent) => {
      onClose?.({ reason: 'outsideMousedown', target: event.target as Element });
    },
    [onClose]
  );

  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (event.defaultPrevented) {
        return;
      }
      switch (event.code) {
        case KEYS.ESC:
        case KEYS.TAB:
          onClose?.({ reason: 'dismissed' });
          break;
        default:
          break;
      }
    },
    [onClose]
  );

  // use the mousedown event to be consistent with legacy JET
  useOutsideMousedown({
    isDisabled: false,
    ref: [dropdownRef],
    handler: handleMousedownClose
  });

  return (
    <>
      {isOpen ? (
        <Layer logicalParentRef={anchorRef as RefObject<Element>}>
          <Floating
            anchorRef={anchorRef}
            ref={dropdownRef}
            placement={placement}
            flipOptions={flipOptions}
            shiftOptions={{
              mainAxis: false,
              crossAxis: false
            }}
            sizeOptions={sizeOptions}
            offsetValue={offset}
            onPosition={onPosition}>
            <div
              id={id}
              class={dropdownStyles.base}
              style={{ minWidth: `${minWidth || 0}px` }}
              onKeyDown={handleKeyDown}>
              {children}
            </div>
          </Floating>
        </Layer>
      ) : null}
    </>
  );
}
